import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MODULE_LABELS } from '../iad/constants';
import { Section, Table } from './parts';
import { BudgetManagerDialog } from './BudgetManager';

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('es', { maximumFractionDigits: 2, notation: Math.abs(v) >= 1e6 ? 'compact' : 'standard' }));
const signed = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);
const tone = (v) => (v == null ? 'text-slate-400' : v > 0 ? 'text-emerald-600 dark:text-emerald-400' : v < 0 ? 'text-red-600 dark:text-red-400' : '');

const CmpCell = ({ c, testid }) => (
  <td className="p-2" data-testid={testid}>
    {c.previous == null ? <span className="text-xs text-slate-400">Sin dato de {c.previous_period}</span> : (
      <><p className={`font-semibold ${tone(c.change_pct)}`}>{signed(c.change_pct)}</p>
        <p className="text-[11px] text-slate-500">{num(c.previous)} ({c.previous_period}) → {num(c.current)} ({c.current_period})</p></>)}
  </td>
);

export const PeriodComparisons = ({ kpis }) => {
  const [all, setAll] = useState(false);
  const rows = kpis.filter((k) => k.comparisons && (all || ['mom', 'qoq', 'yoy'].some((p) => k.comparisons[p].previous != null)));
  return (
    <Section title="Comparativos por periodo" subtitle="Último valor medido de cada mes o trimestre: mes contra mes, trimestre contra trimestre y año contra año" testid="hist-comparisons">
      <Table head={['KPI', 'Mes vs mes', 'Trimestre vs trimestre', 'Año vs año']} empty="Aún no hay dos periodos medidos para comparar."
        rows={rows.map((k) => (
          <tr key={`${k.kpi_name}-${k.module_type}`} className="border-t border-slate-200 dark:border-slate-700" data-testid={`hist-cmp-${k.kpi_name}`}>
            <td className="p-2"><p className="font-medium">{k.kpi_name}</p><p className="text-[11px] text-slate-500">{MODULE_LABELS[k.module_type] || k.module_type || '—'}</p></td>
            <CmpCell c={k.comparisons.mom} testid={`hist-cmp-mom-${k.kpi_name}`} />
            <CmpCell c={k.comparisons.qoq} testid={`hist-cmp-qoq-${k.kpi_name}`} />
            <CmpCell c={k.comparisons.yoy} testid={`hist-cmp-yoy-${k.kpi_name}`} />
          </tr>))} />
      <button type="button" onClick={() => setAll((v) => !v)} className="text-xs text-sky-600 hover:underline dark:text-sky-400" data-testid="hist-cmp-toggle">
        {all ? 'Ocultar KPIs sin periodo anterior' : 'Mostrar también KPIs sin periodo anterior'}</button>
    </Section>
  );
};

const Fav = ({ r }) => {
  if (r.actual == null) return <span className="text-xs text-slate-400">Sin real</span>;
  const cls = r.favorable == null ? 'bg-slate-100 text-slate-700' : r.favorable ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800';
  return <Badge className={cls}>{signed(r.variance_pct)}</Badge>;
};

const BudgetRow = ({ b }) => (
  <tr className="border-t border-slate-200 align-top dark:border-slate-700" data-testid={`hist-bva-${b.kpi_name}`}>
    <td className="p-2"><p className="font-medium">{b.kpi_name}</p><p className="text-[11px] text-slate-500">{b.direction === 'lower' ? 'Menor es mejor' : b.direction === 'higher' ? 'Mayor es mejor' : 'Dirección sin definir'}</p></td>
    <td className="p-2"><div className="flex flex-wrap gap-2">{b.rows.slice(-6).map((r) => (
      <div key={r.period} className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] dark:border-slate-700" data-testid={`hist-bva-${b.kpi_name}-${r.period}`}>
        <p className="font-semibold">{r.period}</p><p>P {num(r.budget)} · R {num(r.actual)}</p><Fav r={r} /></div>))}</div></td>
    <td className="p-2">{b.cumulative ? <><p className={`font-semibold ${tone(b.cumulative.variance_pct)}`}>{signed(b.cumulative.variance_pct)}</p>
      <p className="text-[11px] text-slate-500">P {num(b.cumulative.budget)} · R {num(b.cumulative.actual)}</p></> : '—'}</td>
  </tr>
);

export const BudgetVsActual = ({ companyId, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <Section title="Presupuesto vs real" subtitle="Por mes: presupuesto (P) contra el último valor real medido (R). Verde es favorable según la dirección del KPI." testid="hist-budget">
      <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="hist-budget-manage-btn">Gestionar presupuestos</Button></div>
      <Table head={['KPI', 'Últimos meses', 'Acumulado medido']} empty="Aún no hay presupuestos. Cárgalos a mano, con Excel o por Vector Bridge."
        rows={items.map((b) => <BudgetRow key={b.kpi_name} b={b} />)} />
      {open && <BudgetManagerDialog companyId={companyId} onClose={() => setOpen(false)} />}
    </Section>
  );
};
